import React, { useEffect, useRef, useState } from 'react';
import './MapModal.css';

// Leaflet is loaded from CDN via index.html
const L = window.L;

export default function MapModal({ isOpen, onClose, latitude, longitude, title, deviceBox, deviceBag, deviceSeal, userName }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [leafletError, setLeafletError] = useState(!L);

  useEffect(() => {
    if (!isOpen || !latitude || !longitude) return;
    
    // Check if Leaflet library is loaded
    if (!L) {
      setLeafletError(true);
      return;
    }

    // Initialize map on first render or when coordinates change
    if (!mapInstanceRef.current && mapRef.current) {
      try {
        mapInstanceRef.current = L.map(mapRef.current).setView([latitude, longitude], 15);
      
        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }).addTo(mapInstanceRef.current);

        // Add marker at coordinates
        markerRef.current = L.marker([latitude, longitude]).addTo(mapInstanceRef.current)
          .bindPopup(`
            <div style="font-size: 12px;">
              <strong>${title || 'Scan Location'}</strong><br/>
              ${deviceBag ? `📦 Bag: ${deviceBag}<br/>` : ''}
              ${deviceBox ? `📦 Box: ${deviceBox}<br/>` : ''}
              ${deviceSeal ? `🔐 Seal: ${deviceSeal}<br/>` : ''}
              ${userName ? `👤 User: ${userName}<br/>` : ''}
              📍 ${latitude.toFixed(6)}, ${longitude.toFixed(6)}
            </div>
          `)
          .openPopup();
      } catch (error) {
        console.error('Error initializing map:', error);
        setLeafletError(true);
      }
    } else if (mapInstanceRef.current) {
      // Update map view if coordinates change
      mapInstanceRef.current.setView([latitude, longitude], 15);
      if (markerRef.current) {
        markerRef.current.setLatLng([latitude, longitude]);
      }
    }

    // Handle window resize when fullscreen toggles
    const handleResize = () => {
      if (mapInstanceRef.current) {
        setTimeout(() => {
          mapInstanceRef.current.invalidateSize();
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, latitude, longitude, title, deviceBox, deviceBag, deviceSeal, userName]);

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
    // Delay resize to ensure fullscreen transition completes
    setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    }, 100);
  };

  const handleZoomIn = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.zoomOut();
    }
  };

  const handleClose = () => {
    setIsFullscreen(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={`map-modal-overlay ${isFullscreen ? 'fullscreen-active' : ''}`} onClick={handleClose}>
      <div className={`map-modal ${isFullscreen ? 'fullscreen' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="map-modal-header">
          <h2 className="map-modal-title">{title || 'Location Map'}</h2>
          <div className="map-modal-info">
            {deviceBag && <span className="info-badge">📦 Bag: {deviceBag}</span>}
            {deviceBox && <span className="info-badge">📦 Box: {deviceBox}</span>}
            {deviceSeal && <span className="info-badge">🔐 Seal: {deviceSeal}</span>}
            {userName && <span className="info-badge">👤 {userName}</span>}
          </div>
          <div className="map-modal-controls">
            <button 
              className="map-control-btn zoom-in"
              onClick={handleZoomIn}
              title="Zoom In"
              aria-label="Zoom in"
            >
              +
            </button>
            <button 
              className="map-control-btn zoom-out"
              onClick={handleZoomOut}
              title="Zoom Out"
              aria-label="Zoom out"
            >
              −
            </button>
            <button 
              className="map-control-btn fullscreen-btn"
              onClick={handleFullscreenToggle}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? '⛶' : '⛶'}
            </button>
            <button 
              className="map-control-btn close-btn"
              onClick={handleClose}
              title="Close"
              aria-label="Close map"
            >
              ✕
            </button>
          </div>
        </div>
        <div ref={mapRef} className="map-container">
          {leafletError && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              background: '#f5f5f5',
              color: '#666',
              flexDirection: 'column',
              gap: '10px'
            }}>
              <p>⚠️ Map library failed to load</p>
              <p style={{ fontSize: '12px', marginTop: '10px' }}>
                Coordinates: {latitude?.toFixed(6)}, {longitude?.toFixed(6)}
              </p>
            </div>
          )}
        </div>
        <div className="map-modal-footer">
          <p className="coordinates">📍 {latitude?.toFixed(6)}, {longitude?.toFixed(6)}</p>
        </div>
      </div>
    </div>
  );
}
